import React, { useEffect, useMemo } from "react";
import {
  Button,
  Divider,
  FormFieldSet,
  PageHeader,
  PageTitle,
  showToast,
  useFormHandler,
} from "../../../../components";
import {
  cityStateListOptions,
  countryListOptions,
  jobTerminationNoticeOptions,
  netTermsOptions,
} from "../../../../assets/data/projectData";
import {
  BusinessSize,
  BusinessType,
  RecordStatus,
} from "../../../../assets/data/enumData";
import { useDispatch, useSelector } from "react-redux";
import { useLoginUser } from "../../../../hooks/useLoginUser";
import {
  addToDraft,
  getVendorOneStart,
  updateVendorStart,
} from "../../../../redux/slices/vendor/vendorSlice";
import Locations from "../../ComponentsList/Locations";
import { debounce } from "../../../../utils";

const GeneralInformation = ({
  setActiveTab,
  isEditable,
  editVendor = "",
  vendorId,
}) => {
  const dispatch = useDispatch();
  const vendorProfileUiid = useSelector(
    (state) => state.vendor.vendorProfileUiid
  );
  const vendorDetails = useSelector(
    (state) => state.vendor.currentVendor.vendorDetails
  );
  const { userData: loginUserData } = useLoginUser();
  const loginUserUiid = loginUserData?.uiid;

  // Generate a unique ID for each location
  const generateId = () =>
    `loc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(
      {
        businessSize: "",
        businessType: "",
        recordStatus: "",
        numberOfEmployees: "",
        terminationNotice: "",
        netTerms: "",
        // hrName: "",
        // hrEmail: "",
        locations: [
          {
            id: generateId(),
            address1: "",
            address2: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
            isDefaultInvoice: false,
          },
        ],
      },
      {
        businessSize: { type: "select", required: true },
        recordStatus: { type: "select", required: true },
        businessType: { type: "select", required: true },
        netTerms: { type: "select", required: true },
        // locations: [
        //   {
        //     address1: {
        //       type: "text",
        //       required: true,
        //     },
        //   },
        // ],
      }
    );

  // Fetching the tab data and storing in store
  useEffect(() => {
    if (vendorProfileUiid) {
      console.log("getting data for current client for general info:::::::::");
      dispatch(
        getVendorOneStart({
          vendorUiid: vendorProfileUiid,
          retrieve: `generalInfo`,
        })
      );
    }
  }, [vendorId,vendorProfileUiid]);

  // Handle change for sync with store
  useEffect(() => {
    // if (vendorProfileUiid) {
    if (vendorProfileUiid || Object.keys(vendorDetails).length > 0) {
      console.log(
        "checking store for vendor and updating screen for business Information:::::::::",
        vendorDetails?.generalInfo
      );

      // Ensure locations have IDs
      const locationsWithIds = vendorDetails?.generalInfo?.locations?.map(
        (location) => {
          if (!location.id) {
            return {
              address1: location.address1,
              address2: location.address2,
              country: location.country,
              state: location.state,
              city: location.city,
              zipCode: location.zipCode,
              isDefaultInvoice: location.isDefaultBilling,
              id: generateId(),
            };
          }
          return location;
        }
      ) || [
        {
          id: generateId(),
          address1: "",
          address2: "",
          country: "",
          state: "",
          city: "",
          zipCode: "",
          isDefaultInvoice: false,
        },
      ];

      if (locationsWithIds.length > 0) {
        handleChange("locations", locationsWithIds);
      }

      handleChange(
        "businessSize",
        vendorDetails?.generalInfo?.businessSize || ""
      );
      handleChange(
        "businessType",
        vendorDetails?.generalInfo?.businessType || ""
      );
      handleChange(
        "recordStatus",
        vendorDetails?.generalInfo?.recordStatus || ""
      );
      handleChange(
        "numberOfEmployees",
        vendorDetails?.generalInfo?.employeesCount || ""
      );
      handleChange(
        "terminationNotice",
        vendorDetails?.generalInfo?.terminationNotice || ""
      );
      handleChange("netTerms", vendorDetails?.generalInfo?.netTerms || "");
      // handleChange("hrName", vendorDetails?.generalInfo?.hr?.name || "");
      // handleChange("hrEmail", vendorDetails?.generalInfo?.hr?.email || "");
    }
    // }, [vendorDetails]);
    // }, [vendorProfileUiid, Object.keys(vendorDetails).length]);
  }, [vendorDetails]);

  console.log("formData generali", formData);

  const handleBack = () => {
    setTimeout(() => {
      // Move to back tab
      setActiveTab((prev) => prev - 1);
    }, 500);
  };

  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...formData.locations];
    updatedLocations[index] = { ...updatedLocations[index], [field]: value };
    handleChange("locations", updatedLocations);
  };

  const handleBillingAddressChange = (index) => {
    const currentIsBilling = formData.locations[index].isDefaultInvoice;
    const updatedLocations = formData.locations.map((location, i) => ({
      ...location,
      isDefaultInvoice: i === index ? !currentIsBilling : false,
    }));

    setFormData((prevFormData) => ({
      ...prevFormData,
      locations: updatedLocations,
    }));
  };

  const addLocation = () => {
    const newLocations = [...formData.locations]
      ? [...formData.locations.map((loc) => ({ ...loc }))]
      : [];
    const newLocation = {
      id: generateId(),
      address1: "",
      address2: "",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      isDefaultInvoice: false,
    };
    newLocations.push(newLocation);

    // setFormData((prevFormData) => ({
    //   ...prevFormData,
    //   locations: [...prevFormData.locations, newLocation],
    // }));
    handleChange("locations", newLocations);
  };

  const removeLocation = (id) => {
    if (formData.locations.length > 1) {
      setFormData((prevFormData) => {
        const indexToRemove = prevFormData.locations.findIndex(
          (l) => l.id === id
        );

        if (indexToRemove === -1) {
          return prevFormData; // Return unchanged if ID not found
        }

        const wasBilling =
          prevFormData.locations[indexToRemove].isDefaultInvoice;
        const updatedLocations = prevFormData.locations.filter(
          (l) => l.id !== id
        );

        if (wasBilling && updatedLocations.length > 0) {
          updatedLocations[0].isDefaultInvoice = true;
        }

        return {
          ...prevFormData,
          locations: updatedLocations,
        };
      });
    }
  };

  const handleSave = () => {
    const isValid = validateAllFields();
    try {
      if (isValid) {
        const locationsPayload = {};
        formData.locations.forEach((loc, index) => {
          if (
            loc.address1 ||
            loc.address2 ||
            loc.city ||
            loc.state ||
            loc.zipCode ||
            loc.country ||
            loc.isDefaultInvoice
          ) {
            locationsPayload[`generalInfo.locations.${index}.address1`] =
              loc.address1 || "";
            locationsPayload[`generalInfo.locations.${index}.address2`] =
              loc.address2 || "";
            locationsPayload[`generalInfo.locations.${index}.city`] =
              loc.city || "";
            locationsPayload[`generalInfo.locations.${index}.state`] =
              loc.state || "";
            // Handle zipCode numeric conversion safely
            locationsPayload[`generalInfo.locations.${index}.zipCode`] =
              loc.zipCode && loc.zipCode.toString().length <= 15
                ? Number(loc.zipCode)
                : null;
            locationsPayload[`generalInfo.locations.${index}.country`] =
              loc.country || "";
            locationsPayload[
              `generalInfo.locations.${index}.isDefaultBilling`
            ] = loc.isDefaultInvoice || false;
          }
        });
        console.log({vendorDetails})
        const prepareSubmissionData =
          // !vendorDetails?.generalInfo ||
          // vendorDetails?.generalInfo?.locations.length === 0
          !vendorDetails?.generalInfo?.locations?.length
            ? {
                uiid: vendorProfileUiid,
                coll: `Vendor`,
                payload: [
                  {
                    type: "SET",
                    value: {
                      generalInfo: {
                        employeesCount: Number(formData.numberOfEmployees),
                        terminationNotice: Number(
                          formData.terminationNotice?.value
                        ),
                        netTerms: formData.netTerms?.value || formData.netTerms,
                        // hr: {
                        //   email: formData.hrEmail,
                        //   name: formData.hrName,
                        // },
                        businessSize: Number(formData.businessSize),
                        businessType: Number(formData.businessType),
                        recordStatus: Number(formData.recordStatus),
                      },
                    },
                  },
                  {
                    type: "PUSH",
                    value: {
                      "generalInfo.locations": formData.locations.map(
                        (contact) => ({
                          address1: contact.address1,
                          address2: contact.address2,
                          city: contact.city,
                          comments: contact.comments,
                          country: contact.country,
                          isDefaultBilling: contact.isDefaultInvoice,
                          state: contact.state,
                          zipCode: contact.zipCode,
                        })
                      ),
                    },
                  },
                ],
              }
            : {
                uiid: vendorProfileUiid,
                coll: `Vendor`,
                payload: [
                  {
                    type: "SET",
                    value: {
                      generalInfo: {
                        employeesCount: Number(formData.numberOfEmployees),
                        terminationNotice: Number(
                          formData.terminationNotice?.value
                        ),
                        netTerms: formData.netTerms?.value || formData.netTerms,
                        // hr: {
                        //   email: formData.hrEmail,
                        //   name: formData.hrName,
                        // },
                        businessSize: Number(formData.businessSize),
                        businessType: Number(formData.businessType),
                        recordStatus: Number(formData.recordStatus),
                      },
                      ...locationsPayload,
                    },
                  },
                ],
              };
        dispatch(
          updateVendorStart({
            ...prepareSubmissionData,
            onSuccess: () => {
              dispatch(
                getVendorOneStart({
                  vendorUiid: vendorProfileUiid,
                  retrieve: ` generalInfo`,
                })
              );
              setActiveTab((prev) => prev + 1);
            },
          })
        );
      } else {
        showToast("Please fill all the required fields before saving", "error", 3000);
        console.log("Please fix the errors before submitting.");
      }
    } catch (error) {
      console.error("Error saving general information:", error);
    } finally {
    }
  };

  const handleDraft = useMemo(
    () =>
      debounce((value) => {
        dispatch(
          // addToDraft({
          //   [field]: value ? value : "",
          // })
          addToDraft({ ...value })
        );
      }, 1000),
    [dispatch]
  );
  useEffect(() => {
    let vendorData = {
      generalInfo: {
        employeesCount: Number(formData.numberOfEmployees),
        terminationNotice: formData.terminationNotice,
        netTerms: formData.netTerms,
        // hr: {
        //   email: formData.hrEmail,
        //   name: formData.hrName,
        // },
        businessSize: Number(formData.businessSize),
        businessType: Number(formData.businessType),
        recordStatus: Number(formData.recordStatus),
        locations: formData.locations,
      },
    };
    // currently stopped drafting
    // if (vendorProfileUiid || Object.keys(vendorDetails).length > 0) {
    // handleDraft(vendorData);
    // }
  }, [formData]);
  return (
    <div className="flex flex-col w-full h-full px-8 py-3 overflow-auto rounded">
      <div className="mb-8">
        <PageHeader
          Section={
            <div className="flex">
              <PageTitle
                title={"General Information"}
                icon={"badge"}
                tag="h5"
              />
            </div>
          }
          RightSection={
            <>
              {isEditable && editVendor && (
                <>
                  <Button
                    title="Back"
                    size="sm"
                    icon={"arrow_left_alt"}
                    onClick={handleBack}
                  />
                  <Button title="Save" size="sm" onClick={handleSave} />
                </>
              )}
            </>
          }
        />
      </div>
      <div className="grid xs:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 max-w-7xl">
        <div>
          <FormFieldSet
            type="select"
            title={"Business Size"}
            placeholder="Select Business Size"
            options={BusinessSize}
            value={formData.businessSize}
            onChange={(value) => handleChange("businessSize", value)}
            error={errors.businessSize}
            required
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="select"
            title={"Business Type"}
            placeholder="Select Business Type"
            options={BusinessType}
            value={formData.businessType}
            onChange={(value) => handleChange("businessType", value)}
            error={errors.businessType}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="select"
            title={"Record Status"}
            placeholder="Select Record Status"
            options={RecordStatus}
            value={formData.recordStatus}
            onChange={(value) => handleChange("recordStatus", value)}
            error={errors.recordStatus}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="text"
            title={"Number of Employees"}
            placeholder="Enter No of Employees"
            value={formData.numberOfEmployees}
            onChange={(value) => handleChange("numberOfEmployees", value)}
            error={errors.numberOfEmployees}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="jobTerminationNotice"
            title={"Termination Notice"}
            placeholder="Select "
            options={jobTerminationNoticeOptions}
            value={formData.terminationNotice}
            onChange={(value) => handleChange("terminationNotice", value)}
            error={errors.terminationNotice}
            showSearch
            showButton
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            // type="netTerm"
            type="select"
            title={"Net Terms"}
            placeholder="Select"
            options={netTermsOptions}
            value={formData.netTerms}
            onChange={(value) => handleChange("netTerms", value)}
            error={errors.netTerms}
            // showButton
            showSearch
            disabled={!isEditable}
          />
        </div>
      </div>
      {/* <div className="mt-6 mb-3">
        <PageHeader
          Section={
            <div className="flex">
              <PageTitle title={"HR Details"} tag="h6" />
            </div>
          }
        />
      </div>
      <div className="grid xs:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 max-w-7xl">
        <div>
          <FormFieldSet
            type="text"
            title={"HR Name"}
            placeholder="Enter HR Name"
            value={formData.hrName}
            onChange={(value) => handleChange("hrName", value)}
            error={errors.hrName}
            disabled={!isEditable}
          />
        </div>
        <div>
          <FormFieldSet
            type="text"
            title={"HR Email"}
            placeholder="Enter HR Email"
            value={formData.hrEmail}
            onChange={(value) => handleChange("hrEmail", value)}
            error={errors.hrEmail}
            disabled={!isEditable}
          />
        </div>
      </div> */}

      {/* Locations Section */}
      <div className="mt-6 mb-3">
        <PageHeader
          Section={
            <div className="flex">
              <PageTitle title={"Locations"} tag="h6" />
            </div>
          }
          RightSection={
            <Button
              title="Add more"
              size="sm"
              icon={"add"}
              variant="text"
              color="info"
              onClick={addLocation}
            />
          }
        />
      </div>
      <div className="text-sm text-gray-600 mb-2"></div>
      {formData?.locations?.length > 0 &&
        formData.locations.map((location, index) => (
          <>
            {/* <div key={location.id || `location-${index}`} className="mb-6">
              {index > 0 && (
                <div className="flex items-center justify-between mb-3">
                  <h6 className="text-sm font-medium">Location {index + 1}</h6>
                  <Button
                    title="Remove"
                    size="xs"
                    icon={"remove"}
                    variant="text"
                    color="danger"
                    onClick={() => removeLocation(location.id)}
                  />
                </div>
              )}
              <div className="grid xs:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 max-w-7xl">
                <div>
                  <FormFieldSet
                    type="text"
                    title={"Address Line 1"}
                    placeholder="Enter Address"
                    value={location.address1}
                    onChange={(value) =>
                      handleLocationChange(index, "address1", value)
                    }
                    error={errors.locations?.[index]?.address1}
                    disabled={!isEditable}
                  />
                </div>
                <div>
                  <FormFieldSet
                    type="text"
                    title={"Address Line 2"}
                    placeholder="Enter Address"
                    value={location.address2}
                    onChange={(value) =>
                      handleLocationChange(index, "address2", value)
                    }
                    error={errors.locations?.[index]?.address2}
                    disabled={!isEditable}
                  />
                </div>
                <div>
                  <FormFieldSet
                    type="select"
                    title={"Country"}
                    placeholder="Select Country"
                    options={countryListOptions}
                    value={location.country}
                    onChange={(value) =>
                      handleLocationChange(index, "country", value)
                    }
                    error={errors.locations?.[index]?.country}
                    disabled={!isEditable}
                  />
                </div>
                <div>
                  <FormFieldSet
                    type="select"
                    title={"State"}
                    placeholder="Select State"
                    options={cityStateListOptions}
                    value={location.state}
                    onChange={(value) =>
                      handleLocationChange(index, "state", value)
                    }
                    error={errors.locations?.[index]?.state}
                    disabled={!isEditable}
                  />
                </div>
                <div>
                  <FormFieldSet
                    type="text"
                    title={"City"}
                    placeholder="Enter City"
                    value={location.city}
                    onChange={(value) =>
                      handleLocationChange(index, "city", value)
                    }
                    error={errors.locations?.[index]?.city}
                    disabled={!isEditable}
                  />
                </div>
                <div>
                  <FormFieldSet
                    type="text"
                    title={"Zip Code"}
                    placeholder="Enter Zipcode"
                    value={location.zipCode}
                    onChange={(value) =>
                      handleLocationChange(index, "zipCode", value)
                    }
                    error={errors.locations?.[index]?.zipCode}
                    disabled={!isEditable}
                  />
                </div>
                <div>
                  <FormFieldSet
                    type="checkbox"
                    title=""
                    options={[
                      { label: "Default Billing Address", value: "true" },
                    ]}
                    values={location.isDefaultInvoice ? ["true"] : []}
                    onChange={() => handleBillingAddressChange(index)}
                    error={errors.locations?.[index]?.isDefaultInvoice}
                    multiple
                    disabled={!isEditable}
                  />
                </div>
              </div>
            </div> */}
            <Locations
              index={index}
              formData={formData}
              errors={errors}
              isEditable={isEditable}
              handleLocationChange={handleLocationChange}
              // hasDefaultCheck
              hasDefaultACheck
              location={location}
              handleBillingAddressChange={handleBillingAddressChange}
            />
          </>
        ))}
    </div>
  );
};

export default GeneralInformation;
